import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import {
    PublicOutlined as AllIcon,
    GpsFixedOutlined as SpecificIcon,
    CheckCircle as CheckIcon,
    PlayCircleOutline as VideoIcon,
    PhotoLibraryOutlined as CarouselIcon,
} from '@mui/icons-material';
import { useInstagramMediaInfinite } from '@/hooks/queries/instagram';
import type { InstagramMediaItem } from '@/types/instagram';
import { ACCENT, ACCENT_TINT } from '../_shared';
import type { WizardData } from './wizardTypes';

interface Props {
    data: WizardData;
    onChange: (patch: Partial<WizardData>) => void;
    compact?: boolean;
}

const PickCard = ({
    selected,
    icon,
    title,
    desc,
    onClick,
}: {
    selected: boolean;
    icon: React.ReactNode;
    title: string;
    desc: string;
    onClick: () => void;
}) => (
    <Box
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(ev) => {
            if (ev.key === 'Enter' || ev.key === ' ') onClick();
        }}
        sx={{
            flex: 1,
            bgcolor: selected ? ACCENT_TINT : 'background.paper',
            border: '1.5px solid',
            borderColor: selected ? ACCENT : 'divider',
            borderRadius: 2.5,
            p: 2,
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'border-color 120ms ease, background-color 120ms ease',
            '&:hover': { borderColor: ACCENT },
            '&:focus-visible': { outline: 'none', boxShadow: `0 0 0 3px ${ACCENT_TINT}` },
        }}
    >
        <Box sx={{ color: selected ? ACCENT : 'text.secondary', mb: 0.75 }}>{icon}</Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
            {desc}
        </Typography>
    </Box>
);

/** 캡션 → 상품명(첫 줄) + 나머지(해시태그 등) */
const parseCaption = (caption: string | null): { title: string; sub: string } => {
    const lines = (caption ?? '')
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
    return { title: lines[0] ?? '', sub: lines.slice(1).join(' ') };
};

const MediaThumb = ({
    media,
    selected,
    onToggle,
}: {
    media: InstagramMediaItem;
    selected: boolean;
    onToggle: () => void;
}) => {
    const thumbnail = media.thumbnailUrl || media.mediaUrl;
    const { title, sub } = parseCaption(media.caption);
    return (
        <Box
            role="button"
            tabIndex={0}
            onClick={onToggle}
            onKeyDown={(ev) => {
                if (ev.key === 'Enter' || ev.key === ' ') onToggle();
            }}
            sx={{ cursor: 'pointer', '&:focus-visible': { outline: 'none' } }}
        >
            <Box
                sx={{
                    position: 'relative',
                    aspectRatio: '1 / 1',
                    borderRadius: 2,
                    overflow: 'hidden',
                    border: '2px solid',
                    borderColor: selected ? ACCENT : 'rgba(0,0,0,0.08)',
                    bgcolor: 'action.hover',
                    backgroundImage: thumbnail ? `url("${thumbnail}")` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    transition: 'border-color 120ms ease, transform 120ms ease',
                    '&:hover': { transform: 'translateY(-1px)', borderColor: ACCENT },
                }}
            >
                {media.mediaType === 'VIDEO' && (
                    <VideoIcon sx={{ position: 'absolute', top: 6, left: 6, color: '#fff', fontSize: 18, opacity: 0.9 }} />
                )}
                {media.mediaType === 'CAROUSEL_ALBUM' && (
                    <CarouselIcon
                        sx={{ position: 'absolute', top: 6, left: 6, color: '#fff', fontSize: 18, opacity: 0.9 }}
                    />
                )}
                {/* 선택 표시 (미선택도 원 표시 — 앱과 동일) */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 6,
                        right: 6,
                        width: 24,
                        height: 24,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: selected ? ACCENT : 'rgba(255,255,255,0.9)',
                        border: selected ? 'none' : '1.5px solid rgba(0,0,0,0.15)',
                        color: '#fff',
                    }}
                >
                    {selected && <CheckIcon sx={{ fontSize: 16 }} />}
                </Box>
            </Box>

            {/* 상품명 + 캡션 */}
            <Box sx={{ mt: 0.75, px: 0.25 }}>
                <Typography
                    sx={{ fontSize: 13, fontWeight: 700, color: selected ? ACCENT : 'text.primary', lineHeight: 1.35 }}
                    noWrap
                >
                    {title || '(제목 없음)'}
                </Typography>
                {sub && (
                    <Typography
                        sx={{
                            fontSize: 12,
                            color: 'text.secondary',
                            lineHeight: 1.35,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {sub}
                    </Typography>
                )}
            </Box>
        </Box>
    );
};

const Step4Target = ({ data, onChange, compact }: Props) => {
    const mediaQuery = useInstagramMediaInfinite({
        limit: 24,
        enabled: data.target === 'specific',
    });
    const items = mediaQuery.data?.pages.flatMap((p) => p.items) ?? [];
    const selectedSet = new Set(data.targetPostIds);

    const togglePost = (id: string) => {
        const next = selectedSet.has(id) ? data.targetPostIds.filter((x) => x !== id) : [...data.targetPostIds, id];
        onChange({ targetPostIds: next });
    };

    return (
        <Box>
            {!compact && (
                <>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.5 }}>
                        어떤 게시물에 적용할까요?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
                        자동화가 실행될 게시물 범위를 정해주세요.
                    </Typography>
                </>
            )}

            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                <PickCard
                    selected={data.target === 'all'}
                    icon={<AllIcon sx={{ fontSize: 28 }} />}
                    title="모든 게시물"
                    desc="앞으로 올릴 게시물도 자동 적용"
                    onClick={() => onChange({ target: 'all', targetPostIds: [] })}
                />
                <PickCard
                    selected={data.target === 'specific'}
                    icon={<SpecificIcon sx={{ fontSize: 28 }} />}
                    title="특정 게시물 선택"
                    desc="고른 게시물에만 적용"
                    onClick={() => onChange({ target: 'specific' })}
                />
            </Stack>

            {data.target === 'specific' && (
                <Box sx={{ mt: 2.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
                        게시물 골라주세요 ({data.targetPostIds.length}개 선택됨)
                    </Typography>

                    {mediaQuery.isLoading ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <CircularProgress size={24} sx={{ color: ACCENT }} />
                        </Box>
                    ) : mediaQuery.isError ? (
                        <Typography variant="body2" color="error">
                            게시물을 불러오지 못했어요. 연결 상태를 확인해주세요.
                        </Typography>
                    ) : items.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            게시물이 없어요.
                        </Typography>
                    ) : (
                        <>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
                                    gap: 1.5,
                                }}
                            >
                                {items.map((media) => (
                                    <MediaThumb
                                        key={media.id}
                                        media={media}
                                        selected={selectedSet.has(media.id)}
                                        onToggle={() => togglePost(media.id)}
                                    />
                                ))}
                            </Box>
                            {mediaQuery.hasNextPage && (
                                <Box sx={{ textAlign: 'center', mt: 1.5 }}>
                                    <Button
                                        onClick={() => mediaQuery.fetchNextPage()}
                                        disabled={mediaQuery.isFetchingNextPage}
                                        size="small"
                                        variant="outlined"
                                        sx={{ textTransform: 'none', borderRadius: 2, cursor: 'pointer' }}
                                    >
                                        {mediaQuery.isFetchingNextPage ? '불러오는 중…' : '더 보기'}
                                    </Button>
                                </Box>
                            )}
                        </>
                    )}
                </Box>
            )}
        </Box>
    );
};

export default Step4Target;
